import { useState, useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../../shared/lib/formatters';
import type { ContextSession } from '../../../shared/types/context';
import { useSelectionStore } from '../../state/selectionStore';

export function SessionsPanel() {
  const [sessions, setSessions] = useState<ContextSession[]>([]);
  const [loading, setLoading] = useState(true);
  const sessionId = useSelectionStore((state) => state.session_id);
  const select = useSelectionStore((state) => state.set);
  const pendingSessionRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSessions = async () => {
      try {
        const sessionList = await api().services.context.getSessions(false);
        if (!cancelled) {
          setSessions(sessionList || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[SessionsPanel] Failed to load sessions:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadSessions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    const exists = sessions.some((session) => session.id === sessionId);
    if (exists) {
      return;
    }

    if (pendingSessionRef.current === sessionId) {
      return;
    }

    pendingSessionRef.current = sessionId;
    let cancelled = false;

    const refreshSessions = async () => {
      try {
        const sessionList = await api().services.context.getSessions(false);
        if (!cancelled) {
          setSessions(sessionList || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[SessionsPanel] Failed to refresh sessions:', error);
        }
      } finally {
        if (pendingSessionRef.current === sessionId) {
          pendingSessionRef.current = null;
        }
      }
    };

    refreshSessions();

    return () => {
      cancelled = true;
      if (pendingSessionRef.current === sessionId) {
        pendingSessionRef.current = null;
      }
    };
  }, [sessionId, sessions]);

  const handleSessionClick = async (session: ContextSession) => {
    select({ session_id: session.id });

    // Load messages for this session to find the last message
    try {
      const messages = await api().services.context.getMessages(session.id);
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        select({ message_id: lastMessage.id });
      } else {
        select({ message_id: null });
      }
    } catch (error) {
      console.error('[SessionsPanel] Failed to load messages for session:', error);
      select({ message_id: null });
    }
  };

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>Loading sessions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>No sessions yet</p>
            <p className="empty-state-subtitle">Sessions will appear here once you start a conversation</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-shell">
      <div className="panel-content" style={{ padding: 0 }}>
        <ul className="sessions-list">
          {sessions.map((session, index) => {
            const isSelected = session.id === sessionId;
            return (
              <li key={session.id}>
                <button
                  type="button"
                  onClick={() => handleSessionClick(session)}
                  className={`session-row ${index % 2 === 0 ? 'session-row--even' : 'session-row--odd'} ${isSelected ? 'session-row--selected' : ''}`}
                >
                  <div className="session-row-info">
                    <span className="session-row-id">#{session.id}</span>
                    <span className="session-row-date">{formatDateTime(new Date(session.created_at).toISOString(), '—')}</span>
                    <span className="session-row-title">{session.uuid}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
